import React from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  CardSection,
  CopyComponent,
  CopySpan,
  DataGrid,
  HeaderTitle,
  Label,
  Loading,
  ScreenMask,
  SelectWarehouse,
  message,
} from 'components/common';
import ToDoListInputer from 'components/Inventory/WarehouseTransferWmsReceive/ToDoListInputer';
import SelectWarehouseDialog from 'components/Warehouse/SelectWarehouseDialog';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  HTTP_STATUS_OK,
  LOADING_ICON_SIZE1,
  LS_DEFAULT_WAREHOUSE_KEY,
} from 'constants/config';
import {
  fetchPostedTransferPickListPendingTransfers,
  queuePostedTransferPicklist,
} from 'services/inventory';
import {
  convertTimeByUtc,
  dateSorter,
  getWarehouseFromCache,
  onSelectionChange,
} from 'utils';
import { renderTransferStatusCell } from '.';

const QueuePickList = () => {
  const DATE_SEARCH_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
  const DEFAULT_GRID_PAGE_SIZE = 20;
  const { useRef, useState } = React;
  const [allowReprint, setAllowReprint] = useState(false);
  const [batchList, setBatchList] = useState<StringKAnyVPair[]>([]);
  const [dateRange, setDateRange] = useState<[any, any]>([moment().subtract(30, 'days').startOf('days'), moment().endOf('days')]);
  const [filterColSpan] = useState(8);
  const [gridCount, setGridCount] = useState(0);
  const [inited, setInited] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [searchDateType, setSearchDateType] = useState(0);
  const [searchNumber, setSearchNumber] = useState<string>();
  const [searchNumberType, setSearchNumberType] = useState(3);
  const [selectWarehouseDialogVisible, setSelectWarehouseDialogVisible] = useState(false);
  const [selected, setSelected] = useState<any>({});
  const [selectedRow, setSelectedRow] = React.useState<StringKAnyVPair[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<StringKAnyVPair>({});
  const [toDoListInputerVisible, setToDoListInputerVisible] = useState(false);
  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(DEFAULT_GRID_PAGE_SIZE);

  const columns = [
    {
      header: 'Batch Number',
      //maxWidth: 260,
      minWidth: 160,
      name: 'batchNumber',
      render(row: any) {
        const { data } = row;

        return (
          <CopyComponent value={data.batchNumber}>
            <Typography.Text
              className="text-clickable text-blue cell-1-icon"
              ellipsis={{ tooltip: data.batchNumber }}
              //onClick={() => openBatchDetailDialog(data.postedTransferNum)}
            >
              {data.batchNumber}
            </Typography.Text>
          </CopyComponent>
        );
      },
    },
    {
      header: 'ERP Order Number',
      //maxWidth: 260,
      minWidth: 170,
      name: 'saleOrderNumber',
    },
    {
      header: 'Channel Order Id',
      minWidth: 160,
      name: 'channelOrderId',
      render(row: any) {
        const { data } = row;

        return <CopySpan inGrid value={data.channelOrderId} />;
      },
    },
    {
      header: 'SKU',
      //maxWidth: 260,
      minWidth: 220,
      name: 'skus',
      render(row: any) {
        const { data } = row;

        //return data.skus;
        return <CopySpan inGrid value={data.skus} />;
      },
    },
    {
      flex: 1,
      header: 'Queue Status',
      minWidth: 130,
      name: 'pickListStatus',
      sortable: false,
      render(row: any) {
        const { data } = row;

        return <CopySpan inGrid value={queueStatusLabel(data.pickListStatus)} />;
      },
    },
    {
      flex: 1,
      header: 'Status',
      name: 'status',
      sortable: false,
      minWidth: 100,
      render(row: any) {
        const { data } = row;

        return renderTransferStatusCell(data);
      },
    },
    {
      flex: 1,
      header: 'Update Date',
      minWidth: 190,
      name: 'updateDate',
      sort: dateSorter,
      render(row: any) {
        const { data } = row;

        return convertTimeByUtc(data.updateDate);
      },
    },
    {
      flex: 1,
      header: 'Create Date',
      minWidth: 190,
      name: 'createDate',
      sort: dateSorter,
      render(row: any) {
        const { data } = row;

        return convertTimeByUtc(data.createDate);
      },
    },
    /*{
      header: 'Create By',
      name: 'createBy',
      render(row: any) {
        const { data } = row;

        return (
          <CopySpan
            inGrid
            value={data.createBy}
          />
        );
      },
    },*/
    /*{
      headerAlign: 'start',
      header: 'Action',
      maxWidth: 206,
      minWidth: 206,
      name: '',
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return renderActionCell(data);
      },
    },*/
  ];

  // eslint-disable-next-line
  const bootstrap = async (whNum: number) => {
    const wh = getWarehouseFromCache(whNum);

    if (wh) {
      onChangeWarehouse(wh);
    }
  };

  const clearBatchGrid = () => {
    setBatchList([]);
    setGridCount(0);
  };

  const clearSelectedRow = () => {
    setSelected({});
    setSelectedRow([]);
  };

  const closeSelectWarehouseDialog = () => {
    setSelectWarehouseDialogVisible(false);
  };

  const closeToDoListInputer = () => {
    setToDoListInputerVisible(false);
  };

  const getSearchContents = () => {
    //return searchNumber ? [ searchNumber ] : [];
    return searchNumber ? searchNumber.split(',').map(e => e.trim()) : [];
  };

  const getSearchType = () => {
    const chnts = [3, 4];

    return chnts.indexOf(searchNumberType) > -1 ? 3 : searchNumberType;
  };

  const getTransferRowClass = (row: any) => {
    const { data } = row;

    if (data.status === 0 && data.error) return 'bg-error';
  };

  const loadTransferList = async () => {
    setLockScreen(true);
    try {
      const params: StringKAnyVPair = {
        //$skip: gridPageIndex.current * gridPageSize.current,
        pageIndex: gridPageIndex.current + 1,
        //$top: gridPageSize.current,
        pageSize: gridPageSize.current,
      };
      const pd: StringKAnyVPair = {
        allowReprint,
        warehouseNum: selectedWarehouse.warehouseNum,
        warehouseCode: selectedWarehouse.warehouseCode,
        queryDateType: searchDateType,
        dateRangeType: 0,
        startDate: `${dateRange[0].startOf('days').format(DATE_SEARCH_FORMAT)}`,
        endDate: `${dateRange[1].endOf('days').format(DATE_SEARCH_FORMAT)}`,
        searchType: getSearchType(),
        searchContents: getSearchContents(),
      };

      const res = await fetchPostedTransferPickListPendingTransfers(pd, params);
      //const res: StringKAnyVPair = {total: 0, data: []};

      console.log('res', params, pd, res);
      if (res) {
        setGridCount(res.total);
        setBatchList(res.data);
      } else {
        clearBatchGrid();
      }
    } catch(e) {
    } finally {
      setLockScreen(false);
    }
  };

  const onChangeAllowReprint = (evt: any) => {
    setAllowReprint(evt.target.checked);
  };

  const onChangeSearchDateType = (typ: any) => {
    setSearchDateType(typ);
  };

  const onChangeSearchNumber = (evt: any) => {
    const sn = evt.target.value.trim();

    setSearchNumber(sn);
  };

  const onChangeSearchNumberType = (typ: any) => {
    setSearchNumber('');
    setSearchNumberType(typ);
    setTimeout(() => {
      (typ === 4) && openToDoListInputer();
    }, 0);
  };

  const onChangeWarehouse = (wh: StringKAnyVPair) => {
    setSelectedWarehouse(wh || {});
  };

  const onClickSearchNumberInput = () => {
    if (searchNumberType === 4) {
      openToDoListInputer();
    }
  };

  const onConfirmChnOrderList = (list: string[]) => {
    //setToDoList(list.map(e => generateToDoItem({label: e})));
    setSearchNumber(list.join(','));
  };

  const onLimitChange = (limit: number) => {
    //gridRef?.current.deselectAll();
    if (gridPageSize.current !== limit) {
      if (gridPageIndex.current * limit >= gridCount) gridPageIndex.current = 0;

      gridPageSize.current = limit;
      clearSelectedRow();
      loadTransferList();
    }
  };

  const onSkipChange = (skip: number) => {
    const idx = skip / gridPageSize.current;
    //gridRef?.current.deselectAll();
    //console.log('s', skip, idx);
    if (gridPageIndex.current !== idx) {
      gridPageIndex.current = idx;
      clearSelectedRow();
      loadTransferList();
    }
  };

  const onSearchDateRangerChange = (range: any) => {
    //console.log('ch', range);
    console.log(range[0].format('YYYY-MM-DD'));
    setDateRange(range);
  };

  const onSelectWarehouse = (wh: StringKAnyVPair) => {
    console.log('w', wh);
    if (wh) {
      localStorage.setItem(LS_DEFAULT_WAREHOUSE_KEY, wh.id);
      window.location.reload();
    }
  };

  const openToDoListInputer = () => {
    setToDoListInputerVisible(true);
  };

  const queueAllTransfers = () => {
    const tids = batchList.map(e => e.postedTransferNum);
    //console.log('i', tids);
    queueTransferList({postedTransferNums: tids});
  };

  const queueSelectedTransfers = () => {
    const tids = selectedRow.map(e => e.postedTransferNum);
    //console.log('i', tids);
    queueTransferList({postedTransferNums: tids});
  };

  const queueStatusLabel = (st: number) => {
    let ret = '';

    switch(st) {
      case 0:
        ret = 'Pending';
        break;

      case 1:
        ret = 'Queue';
        break;

      case 2:
        ret = 'Printed';
        break;
    }

    return ret;
  };

  const queueTransferList = async (opt: StringKAnyVPair) => {
    setLockScreen(true);
    try {
      const pd = {
        warehouseCode: selectedWarehouse.warehouseCode,
        warehouseNum: selectedWarehouse.warehouseNum,
        ...opt,
      };
      const { code, isSuccess } = await queuePostedTransferPicklist(pd) as any;

      if (code === HTTP_STATUS_OK && isSuccess) {
        message.success({
          content: 'Queued transfers successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        loadTransferList();
      }
    } catch(e) {
      message.error({
        content: `Queued transfers error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const renderFilterGroup = () => {
    return (
      <Row>
        <Col span={filterColSpan}>
          <Row align="middle">
            <Label className="label-bold tail-colon1">Warehouse</Label>
            <div className="mr-8" style={{width: 'calc(100% - 210px)'}}>
              <SelectWarehouse
                defaultWarehouseNum={selectedWarehouse.warehouseNum}
                onChange={onChangeWarehouse}
                value={selectedWarehouse.warehouseNum}
              />
            </div>
            <Checkbox checked={allowReprint} onChange={onChangeAllowReprint}>Allow Reprint</Checkbox>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle">
            <div style={{ width: 186 }}>
              <Select
                bordered={false}
                className="form-label-selector w-100"
                onChange={onChangeSearchNumberType}
                options={[
                  {label: 'Batch Number', value: 1},
                  {label: 'Reference Number', value: 2},
                  {label: 'Channel Order Id', value: 3},
                  {label: 'Channel Order Id List', value: 4},
                ]}
                size="small"
                value={searchNumberType}
              />
            </div>
            <div style={{width: 'calc(100% - 186px)'}}>
              <Input
                //allowClear={searchNumberType !== 4}
                allowClear
                className={(searchNumberType === 4 && searchNumber) ? 'input-readonly-clearable' : ''}
                onChange={onChangeSearchNumber}
                onClick={onClickSearchNumberInput}
                readOnly={searchNumberType === 4}
                value={searchNumber}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle">
            <div className="w-40">
              <Select
                bordered={false}
                className="form-label-selector w-100"
                onChange={onChangeSearchDateType}
                options={[
                  {label: 'Create Date', value: 0},
                  {label: 'Update Date', value: 1},
                ]}
                size="small"
                value={searchDateType}
              />
            </div>
            <div className="w-60">
              <DatePicker.RangePicker
                defaultValue={dateRange}
                format="MM/DD/YYYY"
                onChange={onSearchDateRangerChange}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}></Col>
        {/*<Col span={filterColSpan}>
          <Row align="middle" className="mt-8">
            <div className="pr-8 text-end w-40">
              <FormLabel>Create By</FormLabel>
            </div>
            <div className="w-60">
              <Input
                allowClear
                onChange={onChangeCreator}
                value={creator}
              />
            </div>
          </Row>
        </Col>*/}
        <Col span={filterColSpan}>
          <Row align="middle" className="mt-8" justify="center">
            <Space>
              <Button
                //disabled={!isSearchEnabled()}
                onClick={loadTransferList}
                type="primary"
              >
                <FileSearchOutlined />
                Preview
              </Button>
            </Space>
          </Row>
        </Col>
      </Row>
    );
  };

  React.useEffect(() => {
    if (!inited) {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );

      if (whNum > 0 /*&& whid*/) {
        bootstrap(whNum);
      } else {
        setSelectWarehouseDialogVisible(true);
      }

      setInited(true);
    }
  }, [bootstrap, inited]);

  return (<>
    <HeaderTitle breadcrumb={['Warehouse Transfer', 'ERP Posted Transfer', 'Queue Pick List']} />
    <CardSection>
      {renderFilterGroup()}
      <div className="mt-16">
        <Row className="mb-8">
          <Space>
            <Button
              disabled={gridCount < 1}
              onClick={queueAllTransfers}
            >
              Queue All Transfers
            </Button>
            <Button
              disabled={selectedRow.length < 1}
              onClick={queueSelectedTransfers}
              type="primary"
            >
              Queue Selected Transfers
            </Button>
          </Space>
        </Row>
        <DataGrid
          checkboxColumn
          columns={columns}
          count={gridCount}
          dataSource={batchList}
          limit={gridPageSize.current}
          onLimitChange={onLimitChange}
          onSelectionChange={(props) => onSelectionChange(props, batchList, setSelected, setSelectedRow, 'postedTransferNum')}
          onSkipChange={onSkipChange}
          pageSizes={[5, 20, 50, 100, 200]}
          pagination
          rowKey="postedTransferNum"
          rowClassName={getTransferRowClass}
          selected={selected}
          style={{height: 'calc(100vh - 288px)', maxHeight: 600}}
        />
      </div>
    </CardSection>
    {selectWarehouseDialogVisible && (
      <SelectWarehouseDialog
        defaultEnable
        //defaultValue={recommendWarehouseNum}
        onClose={closeSelectWarehouseDialog}
        onSelect={onSelectWarehouse}
        title="Select default warehouse"
        visible={selectWarehouseDialogVisible}
      />
    )}
    {toDoListInputerVisible && (
      <ToDoListInputer
        centered
        onCancel={closeToDoListInputer}
        onConfirmToDoList={onConfirmChnOrderList}
        title="Channel Order Id List"
        visible={toDoListInputerVisible}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default QueuePickList;
